/**
 * Generic file upload service for handling various file types
 */

export interface UploadOptions {
  /** Path prefix for the file (e.g., 'avatars', 'documents') */
  pathPrefix?: string
  /** Maximum file size in bytes (default: 5MB) */
  maxSizeBytes?: number
  /** Allowed MIME types (default: images only) */
  allowedTypes?: string[]
}

export interface UploadError extends Error {
  code: 'FILE_TOO_LARGE' | 'INVALID_TYPE' | 'UPLOAD_FAILED' | 'CONVERSION_FAILED'
  details?: unknown
}

export interface TrpcClient {
  storage: {
    upload: {
      mutate: (params: {
        filename: string
        data: string
        contentType: string
        directory?: string
      }) => Promise<{ success: boolean; key: string; url?: string }>
    }
  }
}

/**
 * Converts a File object to base64 string
 */
async function convertFileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      // Remove data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64Data = result.split(',')[1]
      if (!base64Data) {
        reject(new Error('Failed to convert file to base64'))
        return
      }
      resolve(base64Data)
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

/**
 * Validates a file against the provided options
 */
function validateFile(file: File, options: UploadOptions): void {
  const {
    maxSizeBytes = 5 * 1024 * 1024, // 5MB default
    allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  } = options

  // Check file size
  if (file.size > maxSizeBytes) {
    const error: UploadError = new Error(
      `File size (${(file.size / (1024 * 1024)).toFixed(1)}MB) exceeds maximum allowed size (${(maxSizeBytes / (1024 * 1024)).toFixed(1)}MB)`
    ) as UploadError
    error.code = 'FILE_TOO_LARGE'
    throw error
  }

  // Check file type
  if (!allowedTypes.includes(file.type)) {
    const error: UploadError = new Error(
      `File type "${file.type}" is not allowed. Allowed types: ${allowedTypes.join(', ')}`
    ) as UploadError
    error.code = 'INVALID_TYPE'
    throw error
  }
}

/**
 * Generic file upload function
 */
export async function uploadFile(
  file: File,
  trpcClient: TrpcClient,
  options: UploadOptions = {}
): Promise<string> {
  try {
    // Validate the file
    validateFile(file, options)

    // Convert to base64
    const base64 = await convertFileToBase64(file)

    // Upload using tRPC
    const result = await trpcClient.storage.upload.mutate({
      data: base64,
      contentType: file.type,
      filename: file.name,
      directory: options.pathPrefix,
    })

    // Return the URL
    return `/api/storage/${result.key}`
  } catch (error) {
    if (error instanceof Error && 'code' in error) {
      throw error // Re-throw our custom errors
    }

    // Wrap unexpected errors
    const uploadError: UploadError = new Error(
      `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`
    ) as UploadError
    uploadError.code = 'UPLOAD_FAILED'
    uploadError.details = error
    throw uploadError
  }
}

/**
 * Upload an avatar image with avatar-specific defaults
 */
export async function uploadAvatar(file: File, trpcClient: TrpcClient): Promise<string> {
  const avatarOptions: UploadOptions = {
    pathPrefix: 'avatars',
    maxSizeBytes: 2 * 1024 * 1024, // 2MB for avatars
    allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  }

  return uploadFile(file, trpcClient, avatarOptions)
}

/**
 * Upload a document file with document-specific defaults
 */
export async function uploadDocument(file: File, trpcClient: TrpcClient): Promise<string> {
  const documentOptions: UploadOptions = {
    pathPrefix: 'documents',
    maxSizeBytes: 10 * 1024 * 1024, // 10MB for documents
    allowedTypes: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'application/rtf',
    ],
  }

  return uploadFile(file, trpcClient, documentOptions)
}
